<template>
  <div class="dynamic-date-filter">
    <div class="dynamic-date-filter__option" @click="emitRadioChange('empty')">
      <div class="dynamic-date-filter__row">
        <input type="radio" :name="`date-filter-${filter.keys[0]}`" value="empty"
          :checked="radioValue === 'empty'">
        <span class="dynamic-date-filter__title">Empty</span>
      </div>
      <div class="dynamic-date-filter__description">
          <i> No filter date selected</i>
      </div>
    </div>

    <div class="dynamic-date-filter__option" @click="emitRadioChange('dynamic')">
      <div class="dynamic-date-filter__row">
        <input type="radio" :name="`date-filter-${filter.keys[0]}`" value="dynamic"
          :checked="radioValue === 'dynamic'">
        <span class="dynamic-date-filter__title">Dynamic Date</span>
      </div>
    </div>

    <div v-if="radioValue === 'dynamic'" class="dynamic-date-filter__nested">
      <div v-for="preset in presets" :key="preset.id" class="dynamic-date-filter__preset"
        @click="emitPresetSelect(preset.id)">
        <div class="dynamic-date-filter__row">
          <input type="radio" :name="`dynamic-preset-${filter.keys[0]}`" :value="preset.id"
            :checked="isPresetSelected(preset.id)">
          <span>{{ preset.label }}</span>
        </div>
        <div v-if="getPresetPreview(preset.id)" class="dynamic-date-filter__preview">
          {{ getPresetPreview(preset.id) }}
        </div>
      </div>

      <div v-if="isPresetSelected('dynamic_custom')" class="dynamic-date-filter__custom">
        <span class="dynamic-date-filter__custom-prefix">{{ dynamicPrefix }}</span>
        <div class="dynamic-date-filter__custom-number" @keydown="preventMinusKey">
          <CInput ref="customNumberInput" :value="customValue" type="number" :input-min="1" :input-max="customMax"
            @input="emitCustomValueInput" placeholder="1234" />
        </div>
        <div class="dynamic-date-filter__custom-unit">
          <CSelectNew :value="customUnit" @input="emitCustomUnitInput" :options="unitOptions" item-text="text"
            item-value="value" placeholder="Select unit">
          </CSelectNew>
        </div>
      </div>

      <div v-if="noteText" class="dynamic-date-filter__note">
        <strong>Note:</strong> {{ noteText }}
      </div>
    </div>

    <div class="dynamic-date-filter__option" @click="emitRadioChange('absolute')">
      <div class="dynamic-date-filter__row">
        <input type="radio" :name="`date-filter-${filter.keys[0]}`" value="absolute"
          :checked="radioValue === 'absolute'">
        <span class="dynamic-date-filter__title">Static Date</span>
      </div>
      <div class="dynamic-date-filter__description">
          <i>Use a fixed start and end date.</i>
      </div>
    </div>

    <div v-if="radioValue === 'absolute'" class="dynamic-date-filter__static">
      <div class="dynamic-date-filter__static-title">Custom Static Date</div>
      <div class="dynamic-date-filter__static-inputs">
        <div>
          <label class="form-label mb-1 dynamic-date-filter__date-label">Start Date</label>
          <CInput :value="startValue" type="date" placeholder="mm/dd/yyyy"
            style="width: 142px !important;" @input="emitStartDateInput" />
        </div>
        <div v-if="filter.keys.length > 1">
          <label class="form-label mb-1 dynamic-date-filter__date-label">End Date</label>
          <CInput :value="endValue" type="date" placeholder="mm/dd/yyyy"
            :min-date="startValue" style="width: 142px !important;" @input="emitEndDateInput" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import CInput from '../c-input.vue';
import CSelectNew from '../c-select-new.vue';
import { extractDynamicPreset } from '../../shared/report-filter-helpers';

export default {
  name: 'DynamicDateFilter',
  components: { CInput, CSelectNew },
  props: {
    filter: {
      type: Object,
      required: true,
    },
    radioValue: {
      type: String,
      default: 'empty',
    },
    presets: {
      type: Array,
      default: () => [],
    },
    dynamicValue: {
      type: String,
      default: '',
    },
    hasCustomConfig: {
      type: Boolean,
      default: false,
    },
    customValue: {
      type: [String, Number],
      default: '',
    },
    customUnit: {
      type: String,
      default: 'days',
    },
    customMax: {
      type: Number,
      default: 720,
    },
    unitOptions: {
      type: Array,
      default: () => [
        { text: 'days', value: 'days' },
        { text: 'months', value: 'months' },
      ],
    },
    startValue: {
      type: String,
      default: '',
    },
    endValue: {
      type: String,
      default: '',
    },
    noteText: {
      type: String,
      default: '',
    },
    referenceDate: {
      type: Object,
      default: null,
    },
  },
  computed: {
    isForwardDynamicFilter() {
      if (this.filter.dynamic_date_direction === 'forward') return true;
      if (this.filter.dynamic_date_direction === 'backward') return false;
      const name = this.filter.name?.toLowerCase() || '';
      return name.includes('expire');
    },
    dynamicPrefix() {
      return this.isForwardDynamicFilter ? 'Expired in' : 'Last';
    },
  },
  methods: {
    preventMinusKey(event) {
      if (event.key === '-' || event.key === 'e') {
        event.preventDefault();
      }
    },
    emitRadioChange(value) {
      this.$emit('radio-change', this.filter, value);
    },
    emitPresetSelect(presetId) {
      this.$emit('preset-select', this.filter, presetId);
    },
    emitCustomValueInput(value) {
      // Remove any negative sign to prevent negative number input
      const sanitized = String(value).replace('-', '');
      this.$emit('custom-value-input', this.filter, sanitized);
      // input keeps the un-clamped text. Force the DOM to match the prop so the
      // input and the filter chip stay in sync.
      this.$nextTick(() => {
        const inputEl = this.$refs.customNumberInput?.$el?.querySelector('input');
        if (inputEl && String(inputEl.value) !== String(this.customValue)) {
          inputEl.value = this.customValue === '' || this.customValue == null
            ? ''
            : String(this.customValue);
        }
      });
    },
    emitCustomUnitInput(value) {
      this.$emit('custom-unit-input', this.filter, value);
    },
    emitStartDateInput(value) {
      this.$emit('start-date-input', value);
    },
    emitEndDateInput(value) {
      this.$emit('end-date-input', value);
    },
    isPresetSelected(presetId) {
      if (presetId === 'dynamic_custom' && this.hasCustomConfig) return true;
      if (!this.dynamicValue) return false;
      return extractDynamicPreset(this.dynamicValue) === presetId;
    },
    getPresetPreview(presetId) {
      const match = presetId.match(/^fixed_(\d+)_(days|months)$/);
      if (!match) return '';

      const amount = parseInt(match[1], 10);
      const unit = match[2];
      const ref = this.referenceDate ? this.referenceDate.clone() : moment();
      let startDate = ref.clone();
      let endDate = ref.clone();

      if (this.isForwardDynamicFilter) {
        endDate = ref.clone().add(amount, unit);
      } else {
        startDate = ref.clone().subtract(amount, unit);
      }

      return `${startDate.format('MM/DD/YYYY')} - ${endDate.format('MM/DD/YYYY')}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.dynamic-date-filter__custom {
    margin-left: 25px;;
}
.dynamic-date-filter {
    .dynamic-date-filter__nested{
        margin: unset;
    }
  .dynamic-date-filter__description {
    font-size: 14px;
  }

  &__option,
  &__preset {
    margin: 0 -16px;
    cursor: pointer;

    &:hover {
      background-color: #EDF4FE;
    }
  }

  &__option {
    padding: 8px 16px;
  }

  &__preset {
    padding: 4px 16px 10px 40px;
  }

  &__row {
    display: flex;
    align-items: center;
  }

  input[type="radio"] {
    opacity: 1;
    margin-right: 8px;
  }

  &__title {
    font-weight: 500;
  }

  &__description {
    font-size: 12px;
    color: #848484;
    margin-left: 24px;
  }

  &__nested {
    margin: 8px 0;
  }

  &__preview {
    color: #848484;
    font-weight: 700;
    font-size: 13px;
    margin-left: 24px;
    margin-top: 4px;
  }

  &__custom {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
  }

  &__custom-prefix {
    font-size: 14px;
  }

  &__custom-number {
    width: 80px;
  }

  &__custom-unit {
    width: 100px;
  }

  &__note {
    background-color: #F5F5F5;
    border-radius: 4px;
    color: #3F3F3F;
    font-size: 12px;
    margin-top: 12px;
    padding: 8px 12px;
  }

  &__static {
    margin: 8px 0 0 16px;
  }

  &__static-title {
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  &__static-inputs {
    display: flex;
    gap: 16px;
  }

  &__date-label {
    font-size: 12px;
  }
}
</style>
